const comparablePath = (url) => {
  try {
    return new URL(url, window.location.origin).pathname
      .split('/')
      .pop()
      .replace(/_(?:\d+x\d*|x\d+)(?=\.)/i, '')
      .toLowerCase();
  } catch (_) {
    return '';
  }
};

const interactionMediaState = (url, root = document) => {
  const gallery = root.querySelector('media-gallery');
  const expected = comparablePath(url);
  const slides = Array.from(gallery?.querySelectorAll('slideshow-slide') || []);
  const index = slides.findIndex((slide) =>
    Array.from(slide.querySelectorAll('img')).some((image) => comparablePath(image.currentSrc || image.src) === expected)
  );
  const activeSlide = slides.find((slide) => slide.getAttribute('aria-hidden') === 'false');
  const active = Boolean(
    activeSlide &&
      Array.from(activeSlide.querySelectorAll('img')).some(
        (image) => comparablePath(image.currentSrc || image.src) === expected
      )
  );

  return {gallery, slides, index, active};
};

const requestInteractionMedia = (url, root = document) => {
  const {gallery, slides, index, active} = interactionMediaState(url, root);
  if (active) return true;

  if (gallery && index >= 0 && gallery.slideshow?.select) {
    gallery.slideshow.select(index, undefined, {animate: false});
    if (gallery.presentation === 'grid') slides[index]?.scrollIntoView({behavior: 'smooth', block: 'nearest'});
  }

  return false;
};

const showInteractionMedia = (url, attempt = 0, root = document, isCurrent = () => true) => {
  if (!url || !isCurrent()) return false;
  if (requestInteractionMedia(url, root)) return true;

  // Galleries and their slideshow controller connect asynchronously. Keep one
  // bounded retry chain alive until the requested image is actually active.
  if (attempt < 16) {
    window.setTimeout(() => showInteractionMedia(url, attempt + 1, root, isCurrent), 125);
  }
  return false;
};

const configRoot = (element) => element?.closest?.('.shopify-section') || document;

const nativeConfig = (root = document) => {
  const source = root.querySelector('[data-hn-native-interaction-media]');
  try {
    return JSON.parse(source?.textContent || '[]') || [];
  } catch (_) {
    return [];
  }
};

const variantRulesConfig = (root = document) => {
  const source = root.querySelector('[data-hn-variant-rules]');
  try {
    return JSON.parse(source?.textContent || '{}') || {};
  } catch (_) {
    return {};
  }
};

const combinationKey = (values) => values.join('\u001f');

const variantOptionGroups = (picker) => {
  const controls = Array.from(picker.querySelectorAll('select, fieldset'));
  return controls.map((container) => {
    if (container instanceof HTMLSelectElement) {
      return {
        container,
        wrapper: container.closest('.variant-option') || container,
        optionName: container.name?.match(/^options\[(.*)\]$/)?.[1] || container.selectedOptions[0]?.dataset.optionName || '',
        values: Array.from(container.options).map((option) => ({value: option.value, control: option, wrapper: option})),
        selected: container.value,
      };
    }
    const inputs = Array.from(container.querySelectorAll('input[data-option-name]'));
    return {
      container,
      wrapper: container.closest('.variant-option') || container,
      optionName: inputs[0]?.dataset.optionName || '',
      values: inputs.map((input) => ({value: input.value, control: input, wrapper: input.closest('label') || input})),
      selected: inputs.find((input) => input.checked)?.value || '',
    };
  }).filter((group) => group.values.length);
};

const hasVisibleCombination = (groups, hidden, optionIndex, candidate) => {
  const choices = groups.map((group, index) => {
    if (index === optionIndex) return [candidate];
    if (index < optionIndex && group.selected) return [group.selected];
    return group.values.filter((item) => item.configVisible !== false).map((item) => item.value);
  });
  const walk = (index, values) => index === choices.length
    ? !hidden.has(combinationKey(values))
    : choices[index].some((value) => walk(index + 1, [...values, value]));
  return walk(0, []);
};

const applyVariantVisibility = (root = document) => {
  root.querySelectorAll('variant-picker').forEach((picker) => {
    const pickerRoot = configRoot(picker);
    const hidden = new Set(variantRulesConfig(pickerRoot).hiddenCombinations || []);
    const configuredOptions = nativeConfig(pickerRoot);
    const groups = variantOptionGroups(picker);
    groups.forEach((group) => {
      const config = configuredOptions.find((item) => item.name === group.optionName);
      group.config = config;
      group.wrapper.hidden = config?.visible === false;
      group.values.forEach((item) => {
        item.configVisible = config?.values?.find((entry) => entry.value === item.value)?.visible !== false;
      });
    });
    groups.forEach((group, optionIndex) => {
      group.values.forEach((item) => {
        const visible = item.configVisible !== false && hasVisibleCombination(groups, hidden, optionIndex, item.value);
        item.control.disabled = !visible;
        item.control.setAttribute('aria-hidden', visible ? 'false' : 'true');
        item.wrapper.hidden = !visible;
      });
      const selectedItem = group.values.find((item) => item.value === group.selected);
      if (selectedItem?.control.disabled) {
        const fallback = group.values.find((item) => !item.control.disabled);
        if (fallback) {
          if (group.container instanceof HTMLSelectElement) {
            group.container.value = fallback.value;
            group.container.dispatchEvent(new Event('change', {bubbles: true}));
          } else {
            fallback.control.checked = true;
            fallback.control.dispatchEvent(new Event('change', {bubbles: true}));
          }
        }
      }
    });
  });
};

const pendingNativeMedia = new WeakMap();
let nativeMediaSequence = 0;

const settleNativeInteractionMedia = (section, pending) => {
  const maxAttempts = 20;
  const retryDelay = 100;

  const applyIfCurrent = (attempt = 0) => {
    if (pendingNativeMedia.get(section)?.sequence !== pending.sequence) return;

    if (requestInteractionMedia(pending.url, section)) {
      pendingNativeMedia.delete(section);
      return;
    }

    if (attempt < maxAttempts) {
      window.setTimeout(() => applyIfCurrent(attempt + 1), retryDelay);
      return;
    }

    // End the bounded attempt without allowing an older interaction to win.
    if (pendingNativeMedia.get(section)?.sequence === pending.sequence) pendingNativeMedia.delete(section);
  };

  applyIfCurrent();
};

const rememberNativeInteraction = (event) => {
  const target = event.target;
  if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;
  const picker = target.closest('variant-picker');
  if (!picker || !event.isTrusted) return;

  const section = picker.closest('.shopify-section');
  if (!section) return;

  const selectedOption = target instanceof HTMLSelectElement ? target.selectedOptions[0] : target;
  const optionName = selectedOption?.dataset.optionName || target.name?.match(/^options\[(.*)\]$/)?.[1] || '';
  const value = selectedOption?.value || target.value || '';
  const config = nativeConfig(section).find((item) => item.name === optionName);
  const media = config?.linkPhotos === true
    ? config.values?.find((item) => item.value === value)?.media || config.fieldMedia
    : null;

  // Without a configured custom image, leave the gallery entirely to
  // Shopify's native variant-media update instead of restoring an old slide.
  if (!media?.url) {
    pendingNativeMedia.delete(section);
    return;
  }

  pendingNativeMedia.set(section, {
    sequence: ++nativeMediaSequence,
    url: media.url,
  });
};

const applyNativeInteractionAfterVariantUpdate = (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const section = target.closest('.shopify-section');
  const pending = section ? pendingNativeMedia.get(section) : null;
  if (!pending || !event.promise) return;

  event.promise
    .then(() => {
      if (pendingNativeMedia.get(section)?.sequence !== pending.sequence) return;
      settleNativeInteractionMedia(section, pending);
    })
    .catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[hanoss-interaction-media] Variant update rejected:', error);
    });
};

if (!window.__hnInteractionMediaBound) {
  window.__hnInteractionMediaBound = true;

  // Capture the buyer's intent before Shopify's variant picker starts its
  // asynchronous section update. Apply it only after the new gallery lands.
  document.addEventListener('change', rememberNativeInteraction, true);
  document.addEventListener('shopify:product:select', applyNativeInteractionAfterVariantUpdate);

  document.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;

    if (target.matches('[data-hn-personalize-select]')) {
      const option = target.selectedOptions[0];
      showInteractionMedia(option?.dataset.hnMediaUrl || target.dataset.hnFieldMediaUrl || '', 0, configRoot(target));
      return;
    }

    if (target.matches('[data-hn-field-media-url]')) {
      showInteractionMedia(target.dataset.hnFieldMediaUrl || '', 0, configRoot(target));
      return;
    }

    if (!target.closest('variant-picker')) return;
    window.setTimeout(() => applyVariantVisibility(configRoot(target)), 0);
  });

  document.addEventListener('focusin', (event) => {
    const target = event.target;
    if (target instanceof HTMLElement && target.matches('[data-hn-field-media-url]:not(select)')) {
      showInteractionMedia(target.dataset.hnFieldMediaUrl || '', 0, configRoot(target));
    }
  });

  const visibilityTimers = new WeakMap();
  const scheduleVisibility = (root = document) => {
    window.clearTimeout(visibilityTimers.get(root));
    visibilityTimers.set(root, window.setTimeout(() => applyVariantVisibility(root), 50));
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scheduleVisibility(document), {once: true});
  else scheduleVisibility(document);
  const main = document.querySelector('main');
  if (main) new MutationObserver((mutations) => {
    const roots = new Set(mutations.map(({target}) => configRoot(target)));
    roots.forEach(scheduleVisibility);
  }).observe(main, {childList: true, subtree: true});
  document.addEventListener('shopify:section:load', (event) => scheduleVisibility(configRoot(event.target)));
}
